/**
 * Selector / badge de filial activa en el topbar (OLA 5 mejora opcional).
 *
 * Muestra la filial operativa actual del usuario (su `empresa_id`) o, si el
 * usuario es KRATOS multifilial (sin empresa_id o role='admin'), permite
 * cambiar de filial activa para filtrar todas las vistas.
 *
 * La filial activa se persiste en `localStorage` con clave `legatum_filial_activa`
 * y se expone vía un evento global `filial-changed` para que los listados
 * puedan re-cargar al cambiarla.
 */
import { useEffect, useState, useMemo } from "react";
import { ChevronDown, Building2, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "legatum_filial_activa";

export function getFilialActiva() {
  try { return localStorage.getItem(STORAGE_KEY) || ""; }
  catch { return ""; }
}

function setFilialActiva(empresaId) {
  try {
    if (empresaId) localStorage.setItem(STORAGE_KEY, empresaId);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("filial-changed", { detail: { empresa_id: empresaId } }));
}

export default function FilialSelector() {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [activa, setActiva] = useState(getFilialActiva());
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Click-outside para cerrar el dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest("[data-testid='filial-selector']")) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // ¿El usuario puede cambiar de filial? Admin + KRATOS multifilial.
  const puedeCambiar = useMemo(() => {
    if (!user) return false;
    if ((user.role || "").toLowerCase() === "admin") return true;
    if (user.kratos_multifilial === true) return true;
    return false;
  }, [user]);

  useEffect(() => {
    if (!user || !puedeCambiar) return;
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/empresas-grupo");
        if (!cancel) {
          const operativas = (data || []).filter((e) => e.tipo === "operativa" && e.activa !== false);
          setEmpresas(operativas);
        }
      } catch { /* silent — no romper layout */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [user, puedeCambiar]);

  // Detectar la filial del propio usuario (operativos: empresa_id ya fija)
  const filialDelUsuario = empresas.find((e) => e.id === user?.empresa_id);
  const filialActivaData = empresas.find((e) => e.id === activa);

  if (!user) return null;
  if (!puedeCambiar) {
    // Operativo fijo a su filial: solo mostrar badge informativo si existe
    if (!filialDelUsuario) return null;
    return (
      <div
        className="flex items-center gap-1.5 h-9 px-2.5 rounded-full text-xs font-bold bg-[var(--legatum-elevated)] border border-[var(--legatum-border)]"
        data-testid="filial-badge-fijo"
        title={`Filial operativa · ${filialDelUsuario.ccaa || ""}`}
      >
        <Building2 className="h-3.5 w-3.5 text-[#C5A572] shrink-0" />
        <span className="max-w-[90px] sm:max-w-[120px] truncate">{filialDelUsuario.codigo || filialDelUsuario.nombre}</span>
      </div>
    );
  }

  const label = filialActivaData?.codigo || filialActivaData?.nombre || "Todas las filiales";

  return (
    <div className="relative" data-testid="filial-selector">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 h-9 px-2.5 rounded-full text-xs font-bold bg-[var(--legatum-elevated)] border border-[var(--legatum-border)] hover:border-[#C5A572] transition-colors"
        data-testid="filial-selector-btn"
        title="Filtrar todas las vistas por filial / CCAA"
      >
        <Building2 className="h-3.5 w-3.5 text-[#C5A572] shrink-0" />
        <span className="max-w-[90px] sm:max-w-[140px] truncate">{label}</span>
        <ChevronDown className={`h-3 w-3 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-2 z-50 bg-[var(--legatum-surface)] border border-[var(--legatum-border)] rounded-xl shadow-2xl min-w-[260px] max-h-[60vh] overflow-y-auto"
          data-testid="filial-selector-dropdown"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-[var(--legatum-text-muted)] border-b border-[var(--legatum-border)]">
            Filtrar por filial · CCAA
          </div>
          <button
            onClick={() => { setActiva(""); setFilialActiva(""); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--legatum-elevated)] flex items-center gap-2 border-b border-[var(--legatum-border)]"
            data-testid="filial-opt-todas"
          >
            <span className="flex-1 font-bold">Todas las filiales</span>
            {!activa && <Check className="h-3.5 w-3.5 text-[#0F8A5F]" />}
          </button>
          {loading && (
            <div className="px-3 py-3 text-xs italic text-center text-[var(--legatum-text-muted)]">Cargando filiales…</div>
          )}
          {empresas.map((e) => (
            <button
              key={e.id}
              onClick={() => { setActiva(e.id); setFilialActiva(e.id); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--legatum-elevated)] flex items-center gap-2 border-b border-[var(--legatum-border)] last:border-0"
              data-testid={`filial-opt-${e.codigo || e.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{e.nombre}</div>
                <div className="text-[10px] text-[var(--legatum-text-muted)]">{e.codigo} · {e.ccaa || "—"}</div>
              </div>
              {e.id === activa && <Check className="h-3.5 w-3.5 text-[#0F8A5F]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
